import { BASE_URL } from '../components/AppConfig';
import { handleDynamicRequest } from '../components/DyanamicRequest';

function bannerPresignAuthHeaders() {
  const sessionToken =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('sessionToken') : null;
  const headers = { 'Content-Type': 'application/json' };
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;
  return headers;
}

/**
 * Request a presigned URL from the backend, then PUT the file to storage.
 *
 *   POST {BASE_URL}/admin/ads/presigned-url
 *   Body: { mediaName, mediaType } — same contract as /admin/posts/presigned-url.
 *   We pass mediaName = full S3 object key (e.g. ads/MyBanner/default_media.jpg) so paths stay under ads/.
 *
 * @param {File} file
 * @param {string} objectKey S3 key relative to bucket (e.g. ads/MyBanner/default_media.jpg)
 * @returns {Promise<string>} Public HTTPS URL for the uploaded object
 */
export async function uploadBannerFileViaPresignedUrl(file, objectKey) {
  const contentType = file.type || 'application/octet-stream';
  const isVideo = file.type?.startsWith('video/');
  const presignedRes = await fetch(`${BASE_URL}/admin/ads/presigned-url`, {
    method: 'POST',
    credentials: 'include',
    headers: bannerPresignAuthHeaders(),
    body: JSON.stringify({
      mediaName: objectKey,
      mediaType: isVideo ? 'video' : 'image'
    })
  });

  if (presignedRes.status === 401 || presignedRes.status === 403) {
    throw new Error('Unauthorized. Please log in again.');
  }

  const presignedData = await presignedRes.json().catch(() => ({}));
  if (!presignedRes.ok) {
    const msg =
      presignedData?.message || presignedData?.error || `Presign failed (${presignedRes.status})`;
    throw new Error(msg);
  }

  const payload = presignedData?.data ?? presignedData;
  const uploadUrl =
    payload?.presigned_url ??
    payload?.presignedUrl ??
    presignedData?.presignedUrl ??
    presignedData?.data?.presignedUrl;

  const publicUrl =
    payload?.public_url ??
    payload?.url ??
    payload?.image_url ??
    presignedData?.image_url;

  if (!uploadUrl) {
    throw new Error(
      presignedData?.message ||
        presignedData?.error ||
        'Could not get presigned upload URL. Check POST /admin/ads/presigned-url on the API.'
    );
  }

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType }
  });

  if (!putRes.ok) {
    throw new Error('Upload to storage failed');
  }

  return publicUrl || uploadUrl.split('?')[0];
}

export const bannerAdsApi = {
  getAll: async ({ limit, offset } = {}) => {
    const params = new URLSearchParams();
    if (limit != null && limit !== '') params.append('limit', String(limit));
    if (offset != null && offset !== '') params.append('offset', String(offset));
    const qs = params.toString();
    const url = `${BASE_URL}/banner-ads/ads${qs ? `?${qs}` : ''}`;

    return new Promise((resolve) => {
      handleDynamicRequest(
        'GET',
        url,
        null,
        () => resolve({ statusCode: '50000', message: 'Failed to fetch banner ads' }),
        (response) => resolve(response)
      );
    });
  },

  getById: async (id) => {
    return new Promise((resolve) => {
      handleDynamicRequest(
        'GET',
        `${BASE_URL}/banner-ads/ads/${id}`,
        null,
        () => resolve({ statusCode: '50000', message: 'Failed to fetch banner ad' }),
        (response) => resolve(response)
      );
    });
  },

  create: async (data) => {
    return new Promise((resolve) => {
      handleDynamicRequest(
        'POST',
        `${BASE_URL}/banner-ads/ads`,
        data,
        () => resolve({ statusCode: '50000', message: 'Failed to create banner ad' }),
        (response) => resolve(response)
      );
    });
  },

  update: async (id, data) => {
    return new Promise((resolve) => {
      handleDynamicRequest(
        'PUT',
        `${BASE_URL}/banner-ads/ads/${id}`,
        data,
        () => resolve({ statusCode: '50000', message: 'Failed to update banner ad' }),
        (response) => resolve(response)
      );
    });
  },

  delete: async (id) => {
    return new Promise((resolve) => {
      handleDynamicRequest(
        'DELETE',
        `${BASE_URL}/banner-ads/ads/${id}`,
        undefined,
        () => resolve({ statusCode: '50000', message: 'Failed to delete banner ad' }),
        (response) => resolve(response)
      );
    });
  },

  /** Consumer / app: load banners for a section (optional — not used by admin UI) */
  serve: async (placement, { count, cid } = {}) => {
    const params = new URLSearchParams();
    if (count != null && count !== '') params.append('count', String(count));
    if (cid != null && cid !== '') params.append('cid', String(cid));
    const qs = params.toString();
    const url = `${BASE_URL}/banner-ads/serve/${encodeURIComponent(placement)}${qs ? `?${qs}` : ''}`;
    return new Promise((resolve) => {
      handleDynamicRequest(
        'GET',
        url,
        null,
        () => resolve({ statusCode: '50000', message: 'Failed to load banners' }),
        (response) => resolve(response)
      );
    });
  },

  /** Consumer / app: click tracking */
  click: async (adId) => {
    return new Promise((resolve) => {
      handleDynamicRequest(
        'POST',
        `${BASE_URL}/banner-ads/click/${adId}`,
        {},
        () => resolve({ statusCode: '50000', message: 'Failed to record click' }),
        (response) => resolve(response)
      );
    });
  }
};
